import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  emailValidator,
  MobileNoIntValidator,
  requiredValidator,
} from "../../../components/formFields/CommonValidator";
import FormTextField from "../../../components/formFields/FormTextField";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import RippleButton from "../../../components/common/RippleButton";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import {
  createQuotationStep1,
  updateQuotationStep1,
} from "../services/quotation.services";
import {
  clearQuotationInformation,
  increaseActiveStep,
} from "../quotationSlice";
import ShadowCard from "../../../components/common/ShadowCard";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormDatePicker from "../../../components/formFields/FormDateField";
import moment from "moment";
import QuotationAccomodationArray from "./QuotationAccomodationArray";
import { getAllActiveRoomTypes } from "../../roomtype/services/roomType.services";
import { getAllActiveMealTypes } from "../../mealtype/services/mealtype.services";
import QuotationMiscellaneousArray from "./QuotationMiscellaneousArray";
import QuotationTransportArray from "./QuotationTransportArray";
import { getAllActiveVehicleTypes } from "../../vehicletype/services/vehicletype.services";
import { getInquiryByID } from "../../inquiry/services/inquiry.services";
import { insertContactFromOtherPlace } from "../../contact/services/contact.services";
import CountryCodeField from "../../../components/formFields/CountryCodeField";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { ILeadSource } from "../../leadsource/leadsourceModel";
import { NAMEPREFIX, SECTORTYPE } from "../../../_contstants/common";
import { getAllActiveLeadSource } from "../../leadsource/services/leadsource.service";
import { getAllActiveSectors } from "../../sector/services/sector.services";
import { ISector } from "../../sector/sectorModel";
import { ICompany } from "../../company/companyModel";
import { getAllActiveCompanies } from "../../company/services/company.services";
import FormCheckbox from "../../../components/formFields/FormCheckbox";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const TotalChangeWatcher: React.FC<ChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const Transportations = formRenderProps.valueGetter("Transportations");
  const Miscellaneous = formRenderProps.valueGetter("Miscellaneous");
  const TotalAccommodationRate =
    formRenderProps.valueGetter("TotalAccommodationRate") || 0;
  const TotalTransportation = formRenderProps.valueGetter(
    "TotalTransportation"
  );
  const TotalMiscellaneous =
    formRenderProps.valueGetter("TotalMiscellaneous") || 0;
  const DriverAllowance = formRenderProps.valueGetter("DriverAllowance") || 0;
  const TotalTax = formRenderProps.valueGetter("TotalTax") || 0;
  const TotalAmount = formRenderProps.valueGetter("TotalAmount") || 0;
  const SubTotalAmount = formRenderProps.valueGetter("SubTotalAmount") || 0;
  const DiscountAmt = formRenderProps.valueGetter("DiscountAmt") || 0;
  const GstValue = formRenderProps.valueGetter("GstValue") || 0;
  const GstPer = formRenderProps.valueGetter("GstPer") || 0;
  const TCS = formRenderProps.valueGetter("TCS") || 0;
  const isTCS = formRenderProps.valueGetter("isTCS");
  const Type = formRenderProps.valueGetter("Type");

  let total_transport = 0;
  useEffect(() => {
    Transportations &&
      Transportations?.length > 0 &&
      Transportations?.map((item: any) => {
        total_transport += item?.Rate || 0;
      });

    formRenderProps.onChange("TotalTransportation", {
      value: total_transport + +DriverAllowance + +TotalTax || 0,
    });
  }, [
    Transportations?.map((item: any) => item?.Rate)?.join(", "),
    DriverAllowance,
    TotalTax,
  ]);

  let total_miscellaneous = 0;
  useEffect(() => {
    Miscellaneous &&
      Miscellaneous?.length > 0 &&
      Miscellaneous?.map((item: any) => {
        total_miscellaneous += item?.TotalAmount || 0;
      });

    formRenderProps.onChange("TotalMiscellaneous", {
      value: total_miscellaneous,
    });
  }, [Miscellaneous?.map((item: any) => item?.TotalAmount)?.join(", ")]);

  useEffect(() => {
    formRenderProps.onChange("TotalAmount", {
      value:
        +TotalAccommodationRate + +TotalTransportation + +TotalMiscellaneous ||
        0,
    });
  }, [TotalAccommodationRate, TotalTransportation, TotalMiscellaneous]);

  useEffect(() => {
    formRenderProps.onChange("SubTotalAmount", {
      value: +TotalAmount - +DiscountAmt || 0,
    });
  }, [DiscountAmt, TotalAmount]);

  useEffect(() => {
    formRenderProps.onChange("GstValue", {
      value: (+SubTotalAmount * +GstPer) / 100 || 0,
    });
  }, [SubTotalAmount, GstPer]);

  useEffect(() => {
    const totalAmount = +SubTotalAmount + +GstValue;
    const TCSPer = 5;

    if (Type === "INTERNATIONAL") {
      if (isTCS) {
        if (totalAmount <= 1000000) {
          formRenderProps.onChange("TCS", {
            value: ((+totalAmount * TCSPer) / 100)?.toFixed(2),
          });
        } else {
          const firstTCSAmount = (1000000 * TCSPer) / 100;
          const remainingTCSAmount = +totalAmount - 1000000;
          const secondTCSAmount = (+remainingTCSAmount * 20) / 100;

          formRenderProps.onChange("TCS", {
            value: +(+firstTCSAmount + +secondTCSAmount)?.toFixed(2),
          });
        }
      } else {
        formRenderProps.onChange("TCS", {
          value: 0,
        });
      }
    } else {
      formRenderProps.onChange("TCS", {
        value: 0,
      });
    }
  }, [SubTotalAmount, GstValue, isTCS, Type]);

  useEffect(() => {
    formRenderProps.onChange("FinalAmt", {
      value: Math.round(+SubTotalAmount + +GstValue + +TCS || 0),
    });
  }, [SubTotalAmount, GstValue, TCS]);

  useEffect(() => {
    formRenderProps.onChange("RoundOff", {
      value:
        (SubTotalAmount + +GstValue + +TCS || 0) -
        Math.round(+SubTotalAmount + +GstValue + +TCS || 0),
    });
  }, [SubTotalAmount, GstValue, TCS]);

  return null;
};

const QuotationInformation: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const QuotationID = location.state?.QuotationID;
  const InquiryID = location.state?.InquiryID;

  const loading = useAppSelector((state) => state.quotation.loading);
  const QuotationDetail = useAppSelector(
    (state) => state.quotation.QuotationDetail
  );
  const InquiryDetails = useAppSelector(
    (state) => state.inquiry.InquiryDetails
  );
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList
  );
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const CompanyList = useAppSelector((state) => state.company.CompanyList);

  const [formKey, setFormKey] = useState(1);
  const [initialValues, setInitialValues] = useState<any>({});

  useEffect(() => {
    dispatch(getAllActiveRoomTypes());
    dispatch(getAllActiveMealTypes());
    dispatch(getAllActiveVehicleTypes());
    dispatch(getAllActiveLeadSource());
    dispatch(getAllActiveSectors());
    dispatch(getAllActiveCompanies());
    // if (QuotationID) {
    //   dispatch(getQuotationByID(+QuotationID));
    // } else
    if (!QuotationID && InquiryID) {
      dispatch(getInquiryByID(InquiryID));
    }
    return () => {
      dispatch(clearQuotationInformation());
    };
  }, []);

  useEffect(() => {
    if (InquiryDetails?.ID) {
      setInitialValues({
        // Name: `${InquiryDetails?.FirstName} ${InquiryDetails?.LastName}`,
        Initial: InquiryDetails?.Initial,
        FirstName: InquiryDetails?.FirstName,
        LastName: InquiryDetails?.LastName,
        MobileNo: InquiryDetails?.MobileNo,
        EmailID: InquiryDetails?.EmailID,
        ContactID: InquiryDetails?.ContactID,
        LeadID: InquiryDetails?.LeadID,
        InquiryID: InquiryDetails?.ID,
      });
    }
  }, [InquiryDetails]);

  useEffect(() => {
    setInitialValues(QuotationDetail);
  }, [QuotationDetail]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [initialValues]);

  const handleSubmit = async (values: any) => {
    let contactID = values?.ContactID ? +values?.ContactID : null;
    const insertContactPayload = {
      Initial: values?.Initial ? values?.Initial : "",
      FirstName: values?.FirstName ? values?.FirstName : "",
      LastName: values?.LastName ? values?.LastName : "",
      MobileNo: values?.MobileNo ? values?.MobileNo : "",
      CountryCode: values?.CountryCode ? values?.CountryCode : "",
      EmailID: values?.EmailID ? values?.EmailID : "",
      LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
    };

    if (!QuotationID && !InquiryID) {
      const response = await dispatch(
        insertContactFromOtherPlace(insertContactPayload)
      );

      if (
        response.meta.requestStatus === "fulfilled" &&
        response.payload?.Data?.ID
      ) {
        contactID = +response.payload.Data.ID || null;
      } else {
        return;
      }
    }

    try {
      if (QuotationID) {
        const editPayload = {
          ID: QuotationID ? +QuotationID : null,
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? values?.MobileNo : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          StartDate: values?.StartDate
            ? moment(values?.StartDate)?.format("YYYY-MM-DD")
            : "",
          EndDate: values?.EndDate
            ? moment(values?.EndDate)?.format("YYYY-MM-DD")
            : "",
          PickUpPlace: values?.PickUpPlace ? values?.PickUpPlace : "",
          PickUpDate: values?.PickUpDate
            ? moment(values?.PickUpDate)?.format("YYYY-MM-DD")
            : null,
          DropPlace: values?.DropPlace ? values?.DropPlace : "",
          DropDate: values?.DropDate
            ? moment(values?.DropDate)?.format("YYYY-MM-DD")
            : null,
          TotalRoom: values?.TotalRoom ? +values?.TotalRoom : null,
          TotalAdult: values?.TotalAdult ? +values?.TotalAdult : null,
          TotalCWB: values?.TotalCWB ? +values?.TotalCWB : null,
          TotalCNB: values?.TotalCNB ? +values?.TotalCNB : null,
          TotalAccommodationRate: values?.TotalAccommodationRate
            ? +values?.TotalAccommodationRate
            : null,
          DriverAllowance: values?.DriverAllowance
            ? +values?.DriverAllowance
            : null,
          TotalTax: values?.TotalTax ? +values?.TotalTax : null,
          TotalTransportation: values?.TotalTransportation
            ? +values?.TotalTransportation
            : null,
          TotalMiscellaneous: values?.TotalMiscellaneous
            ? +values?.TotalMiscellaneous
            : null,

          Initial: values?.Initial ? values?.Initial : "",
          LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
          SectorID: values?.SectorID ? +values?.SectorID : null,
          CompanyID: values?.CompanyID ? +values?.CompanyID : null,
          Type: values?.Type ? values?.Type : "",
          DiscountAmt: values?.DiscountAmt ? +values?.DiscountAmt : null,
          GstPer: values?.GstPer ? +values?.GstPer : null,
          GstValue: values?.GstValue ? +(+values?.GstValue?.toFixed(2)) : null,
          isTCS: values?.isTCS ? values?.isTCS : false,
          TCS: values?.TCS ? +values?.TCS : null,
          FinalAmt: values?.FinalAmt ? +(+values?.FinalAmt?.toFixed(2)) : null,

          Accommodations: values?.Accommodations
            ? values?.Accommodations?.map((item: any) => {
                return {
                  Place: item?.Place ? item?.Place : "",
                  Hotel: item?.Hotel ? item?.Hotel : "",
                  CheckInDate: item?.CheckInDate
                    ? moment(item?.CheckInDate)?.format("YYYY-MM-DD")
                    : null,
                  CheckOutDate: item?.CheckOutDate
                    ? moment(item?.CheckOutDate)?.format("YYYY-MM-DD")
                    : null,
                  RoomTypeID: item?.RoomTypeID ? +item?.RoomTypeID : null,
                  MealTypeID: item?.MealTypeID ? +item?.MealTypeID : null,
                  TotalRoom: item?.TotalRoom ? +item?.TotalRoom : null,
                };
              })
            : [],
          Transportations: values?.Transportations
            ? values?.Transportations?.map((item: any) => {
                return {
                  VehicleTypeID: item?.VehicleTypeID
                    ? +item?.VehicleTypeID
                    : null,
                  Place: item?.Place ? item?.Place : "",
                  FromDate: item?.FromDate
                    ? moment(item?.FromDate)?.format("YYYY-MM-DD")
                    : null,
                  ToDate: item?.ToDate
                    ? moment(item?.ToDate)?.format("YYYY-MM-DD")
                    : null,
                  Rate: item?.Rate ? +item?.Rate : null,
                };
              })
            : [],
          Miscellaneous: values?.Miscellaneous
            ? values?.Miscellaneous?.map((item: any) => {
                return {
                  Title: item?.Title ? item?.Title : "",
                  TotalAmount: item?.TotalAmount ? +item?.TotalAmount : null,
                };
              })
            : [],
        };
        const response = await dispatch(updateQuotationStep1(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          // navigate("/quotation/edit", {
          //   state: {
          //     QuotationID: QuotationID,
          //     mode: "create",
          //   },
          // });
          dispatch(increaseActiveStep());
        }
      } else {
        const insertPayload = {
          ContactID: contactID,
          LeadID: values?.LeadID ? +values?.LeadID : null,
          InquiryID: values?.InquiryID ? +values?.InquiryID : null,
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? values?.MobileNo : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          StartDate: values?.StartDate
            ? moment(values?.StartDate)?.format("YYYY-MM-DD")
            : null,
          EndDate: values?.EndDate
            ? moment(values?.EndDate)?.format("YYYY-MM-DD")
            : null,
          PickUpPlace: values?.PickUpPlace ? values?.PickUpPlace : "",
          PickUpDate: values?.PickUpDate
            ? moment(values?.PickUpDate)?.format("YYYY-MM-DD")
            : null,
          DropPlace: values?.DropPlace ? values?.DropPlace : "",
          DropDate: values?.DropDate
            ? moment(values?.DropDate)?.format("YYYY-MM-DD")
            : null,
          TotalRoom: values?.TotalRoom ? +values?.TotalRoom : null,
          TotalAdult: values?.TotalAdult ? +values?.TotalAdult : null,
          TotalCWB: values?.TotalCWB ? +values?.TotalCWB : null,
          TotalCNB: values?.TotalCNB ? +values?.TotalCNB : null,
          TotalAccommodationRate: values?.TotalAccommodationRate
            ? +values?.TotalAccommodationRate
            : null,
          DriverAllowance: values?.DriverAllowance
            ? +values?.DriverAllowance
            : null,
          TotalTax: values?.TotalTax ? +values?.TotalTax : null,
          TotalTransportation: values?.TotalTransportation
            ? +values?.TotalTransportation
            : null,
          TotalMiscellaneous: values?.TotalMiscellaneous
            ? +values?.TotalMiscellaneous
            : null,

          Initial: values?.Initial ? values?.Initial : "",
          LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
          SectorID: values?.SectorID ? +values?.SectorID : null,
          CompanyID: values?.CompanyID ? +values?.CompanyID : null,
          Type: values?.Type ? values?.Type : "",
          DiscountAmt: values?.DiscountAmt ? +values?.DiscountAmt : null,
          GstPer: values?.GstPer ? +values?.GstPer : null,
          GstValue: values?.GstValue ? +(+values?.GstValue?.toFixed(2)) : null,
          isTCS: values?.isTCS ? values?.isTCS : false,
          TCS: values?.TCS ? +values?.TCS : null,
          FinalAmt: values?.FinalAmt ? +(+values?.FinalAmt?.toFixed(2)) : null,

          Accommodations: values?.Accommodations
            ? values?.Accommodations?.map((item: any) => {
                return {
                  Place: item?.Place ? item?.Place : "",
                  Hotel: item?.Hotel ? item?.Hotel : "",
                  CheckInDate: item?.CheckInDate
                    ? moment(item?.CheckInDate)?.format("YYYY-MM-DD")
                    : null,
                  CheckOutDate: item?.CheckOutDate
                    ? moment(item?.CheckOutDate)?.format("YYYY-MM-DD")
                    : null,
                  RoomTypeID: item?.RoomTypeID ? +item?.RoomTypeID : null,
                  MealTypeID: item?.MealTypeID ? +item?.MealTypeID : null,
                  TotalRoom: item?.TotalRoom ? +item?.TotalRoom : null,
                };
              })
            : [],
          Transportations: values?.Transportations
            ? values?.Transportations?.map((item: any) => {
                return {
                  VehicleTypeID: item?.VehicleTypeID
                    ? +item?.VehicleTypeID
                    : null,
                  Place: item?.Place ? item?.Place : "",
                  FromDate: item?.FromDate
                    ? moment(item?.FromDate)?.format("YYYY-MM-DD")
                    : null,
                  ToDate: item?.ToDate
                    ? moment(item?.ToDate)?.format("YYYY-MM-DD")
                    : null,
                  Rate: item?.Rate ? +item?.Rate : null,
                };
              })
            : [],
          Miscellaneous: values?.Miscellaneous
            ? values?.Miscellaneous?.map((item: any) => {
                return {
                  Title: item?.Title ? item?.Title : "",
                  TotalAmount: item?.TotalAmount ? +item?.TotalAmount : null,
                };
              })
            : [],
        };
        const response = await dispatch(createQuotationStep1(insertPayload));
        if (
          response?.meta?.requestStatus === "fulfilled" &&
          response.payload.Data?.ID
        ) {
          navigate("/quotation/edit", {
            state: {
              QuotationID: response.payload.Data?.ID,
              mode: "create",
            },
          });
          dispatch(increaseActiveStep());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      key={formKey}
      initialValues={initialValues}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement>
          <ShadowCard style={{ padding: 10 }}>
            <GridLayout
              style={{ marginRight: 20 }}
              gap={{ rows: 0, cols: 10 }}
              cols={[
                { width: "33.33%" },
                { width: "33.33%" },
                { width: "33.33%" },
              ]}
            >
              <GridLayoutItem colSpan={3}>
                <Typography.h4 className="typography-head">
                  {"Quotation Details"}
                </Typography.h4>
              </GridLayoutItem>
              {QuotationID || InquiryID ? (
                <GridLayoutItem style={{ width: "100%" }} colSpan={3}>
                  <table className="w-100">
                    <tr>
                      <th>Name</th>
                      <td>:</td>
                      <td>
                        {initialValues?.Initial} {initialValues?.FirstName}{" "}
                        {initialValues?.LastName}
                      </td>
                    </tr>
                    <tr>
                      <th>Mobile No.</th>
                      <td>:</td>
                      <td>{initialValues?.MobileNo}</td>
                      <th>Email ID</th>
                      <td>:</td>
                      <td>{initialValues?.EmailID}</td>
                    </tr>
                  </table>
                  <hr className="mb-1" />
                </GridLayoutItem>
              ) : (
                <>
                  <GridLayoutItem className="d-flex gap-2">
                    <Field
                      wrapperStyle={{
                        width: "100%",
                        flex: 1,
                      }}
                      id={"Initial"}
                      name={"Initial"}
                      label={"Initial"}
                      placeholder="i.e. Mr."
                      component={FormSelectionField}
                      options={NAMEPREFIX?.map((initial: any) => {
                        return {
                          value: initial?.value,
                          label: initial?.label,
                        };
                      })}
                    />
                    <Field
                      wrapperStyle={{
                        width: "100%",
                        flex: 2,
                      }}
                      name="FirstName"
                      label="First Name"
                      placeholder="i.e.John"
                      maxLength="100"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="LastName"
                      label="Last Name"
                      placeholder="i.e. Smith"
                      maxLength="100"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="EmailID"
                      label="Email ID"
                      astrike={true}
                      placeholder="i.e. example@gmail.com"
                      component={FormTextField}
                      validator={emailValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem className="mb-1">
                    <Field
                      wrapperStyle={{
                        width: "100%",
                        flex: 1,
                      }}
                      name="CountryCode"
                      label="Code"
                      astrike={true}
                      placeholder="i.e. In"
                      component={CountryCodeField}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{
                        width: "100%",
                        flex: 2,
                      }}
                      name="MobileNo"
                      label="Mobile No."
                      type="number"
                      max={16}
                      placeholder="i.e. 9999999999"
                      component={FormTextField}
                      astrike={true}
                      validator={MobileNoIntValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="w-100"
                      id={"LeadSourceID"}
                      name={"LeadSourceID"}
                      label={"Lead Source"}
                      placeholder="Lead Source"
                      component={FormSelectionField}
                      astrike={true}
                      validator={requiredValidator}
                      options={LeadSourceList?.map(
                        (leadsource: ILeadSource) => {
                          return {
                            value: leadsource?.ID,
                            label: leadsource?.Source,
                          };
                        }
                      )}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <hr className="mb-1" />
                  </GridLayoutItem>
                </>
              )}
              <GridLayoutItem>
                <Field
                  wrapperClassName="w-100"
                  id={"SectorID"}
                  name={"SectorID"}
                  label={"Sector"}
                  placeholder="Sector"
                  component={FormSelectionField}
                  astrike={true}
                  validator={requiredValidator}
                  options={SectorList?.map((sector: ISector) => {
                    return {
                      value: sector?.ID,
                      label: sector?.SectorName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="w-100"
                  id={"CompanyID"}
                  name={"CompanyID"}
                  label={"Company"}
                  placeholder="Company"
                  component={FormSelectionField}
                  astrike={true}
                  validator={requiredValidator}
                  options={CompanyList?.map((company: ICompany) => {
                    return {
                      value: company?.ID,
                      label: company?.CompanyName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="w-100"
                  id={"Type"}
                  name={"Type"}
                  label={"Type"}
                  placeholder="Type"
                  component={FormSelectionField}
                  astrike={true}
                  validator={requiredValidator}
                  options={SECTORTYPE?.map((type: any) => {
                    return {
                      value: type?.value,
                      label: type?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="StartDate"
                  label="Start Date"
                  format="dd/MM/yyyy"
                  component={FormDatePicker}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="EndDate"
                  label="End Date"
                  format="dd/MM/yyyy"
                  astrike={true}
                  validator={requiredValidator}
                  minDate={moment(
                    formRenderProps.valueGetter("StartDate")
                  ).toDate()}
                  component={FormDatePicker}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="PickUpPlace"
                  label="PickUp Place"
                  placeholder="PickUp Place"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="PickUpDate"
                  label="Pickup Date"
                  format="dd/MM/yyyy"
                  component={FormDatePicker}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="DropPlace"
                  label="Drop Place"
                  placeholder="Drop Place"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="DropDate"
                  label="Drop Date"
                  format="dd/MM/yyyy"
                  minDate={moment(
                    formRenderProps.valueGetter("PickUpDate")
                  ).toDate()}
                  component={FormDatePicker}
                />
              </GridLayoutItem>
              <GridLayoutItem className="d-flex gap-2">
                <Field
                  wrapperClassName="right-aligned-field w-100"
                  name="TotalRoom"
                  label="Total Room"
                  placeholder="0"
                  max={50}
                  format="n0"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
                {/* </GridLayoutItem>
              <GridLayoutItem> */}
                <Field
                  wrapperClassName="right-aligned-field w-100"
                  name="TotalAdult"
                  label="Total Adult"
                  placeholder="0"
                  max={50}
                  format="n0"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem className="d-flex gap-2">
                <Field
                  wrapperClassName="right-aligned-field w-100"
                  name="TotalCWB"
                  label="Total CWB"
                  placeholder="0"
                  max={50}
                  format="n0"
                  component={FormNumericTextField}
                />
                {/* </GridLayoutItem>
              <GridLayoutItem> */}
                <Field
                  wrapperClassName="right-aligned-field w-100"
                  name="TotalCNB"
                  label="Total CNB"
                  placeholder="0"
                  max={50}
                  format="n0"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
            </GridLayout>
          </ShadowCard>
          <ShadowCard style={{ padding: 10, marginTop: 10 }}>
            <GridLayout
              style={{ marginRight: 20 }}
              gap={{ rows: 10, cols: 10 }}
              cols={[
                { width: "33.33%" },
                { width: "33.33%" },
                { width: "33.33%" },
              ]}
            >
              <GridLayoutItem colSpan={3}>
                <Typography.h4 className="typography-head m-0">
                  {"Accomodation Details"}
                </Typography.h4>
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="right-aligned-field"
                  name="TotalAccommodationRate"
                  label="Total Rate"
                  placeholder="0"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={3}>
                <FieldArray
                  formRenderProps={formRenderProps}
                  component={QuotationAccomodationArray}
                  name="Accommodations"
                />
              </GridLayoutItem>
            </GridLayout>
          </ShadowCard>
          <ShadowCard style={{ padding: 10, marginTop: 10 }}>
            <GridLayout
              style={{ marginRight: 20 }}
              gap={{ rows: 10, cols: 10 }}
              cols={[
                { width: "33.33%" },
                { width: "33.33%" },
                { width: "33.33%" },
              ]}
            >
              <GridLayoutItem colSpan={3}>
                <Typography.h4 className="typography-head m-0">
                  {"Transport Details"}
                </Typography.h4>
              </GridLayoutItem>
              <GridLayoutItem colSpan={3}>
                <FieldArray
                  formRenderProps={formRenderProps}
                  component={QuotationTransportArray}
                  name="Transportations"
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="right-aligned-field m-0"
                  name="DriverAllowance"
                  label="Driver Allowance"
                  placeholder="0"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="right-aligned-field m-0"
                  name="TotalTax"
                  label="Total Tax"
                  placeholder="0"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="calculation-label right-aligned-field"
                  wrapperStyle={{ margin: 0, width: "100%" }}
                  label={"Total"}
                  name="TotalTransportation"
                  format="n2"
                  placeholder="0"
                  component={FormNumericTextField}
                  disabled="true"
                />
              </GridLayoutItem>
            </GridLayout>
          </ShadowCard>
          <ShadowCard style={{ padding: 10, marginTop: 10 }}>
            <GridLayout
              style={{ marginRight: 20 }}
              gap={{ rows: 10, cols: 10 }}
              cols={[
                { width: "33.33%" },
                { width: "33.33%" },
                { width: "33.33%" },
              ]}
            >
              <GridLayoutItem colSpan={3}>
                <Typography.h4 className="typography-head m-0">
                  {"Miscellaneous Details"}
                </Typography.h4>
              </GridLayoutItem>
              <GridLayoutItem colSpan={3}>
                <FieldArray
                  formRenderProps={formRenderProps}
                  component={QuotationMiscellaneousArray}
                  name="Miscellaneous"
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={3}>
                <div
                  style={{
                    width: "100%",
                    borderTop: "1px dashed gray",
                    paddingTop: "20px",
                    marginTop: "0px",
                  }}
                >
                  <table style={{ width: "100%" }}>
                    <tr>
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                        }}
                      >
                        Total Accomodation <code>(A)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="TotalAccommodationRate"
                          format="n2"
                          placeholder="0"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                        }}
                      >
                        Total Transport <code>(B)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="TotalTransportation"
                          format="n2"
                          placeholder="0"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          paddingBottom: 5,
                        }}
                      >
                        Total Miscellaneous <code>(C)</code>
                      </td>
                      <td
                        style={{
                          width: "5%",
                          textAlign: "center",
                          paddingBottom: 5,
                        }}
                      >
                        :
                      </td>
                      <td style={{ width: 200, paddingBottom: 5 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="TotalMiscellaneous"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderTop: "1px solid gainsboro",
                      }}
                    >
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "5px 0",
                        }}
                      >
                        Total <code>(D) = (A + B + C)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}></td>
                      <td style={{ width: 200, padding: "5px 0 0" }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="TotalAmount"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderBottom: "1px solid gainsboro",
                      }}
                    >
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "10px 0",
                        }}
                      >
                        Discount <code>(E)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="DiscountAmt"
                          placeholder="0"
                          format="n2"
                          max={formRenderProps.valueGetter("TotalAmount")}
                          component={FormNumericTextField}
                        />
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderTop: "1px solid gainsboro",
                      }}
                    >
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "10px 0",
                        }}
                      >
                        Total <code>(F) = (D - E)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="SubTotalAmount"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderTop: "1px solid gainsboro",
                      }}
                    >
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "5px 0",
                        }}
                      >
                        <div className="d-flex gap-1 align-items-center justify-content-end">
                          GST (%) <code>(G)</code>
                          <Field
                            wrapperClassName="calculation-label right-aligned-field"
                            wrapperStyle={{ margin: 0, maxWidth: 100 }}
                            name="GstPer"
                            placeholder="0"
                            format="n2"
                            max={100}
                            component={FormNumericTextField}
                          />
                        </div>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="GstValue"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "5px 0",
                        }}
                      >
                        <Field
                          name="isTCS"
                          label="is TCS Apply"
                          component={FormCheckbox}
                          wrapperStyle={{
                            fontWeight: 600,
                            margin: 0,
                            display: "inline-block",
                          }}
                          style={{ border: "1px solid darkgray" }}
                        />{" "}
                        <code>(H)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200, padding: "0 0 5px" }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="TCS"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "5px 0",
                        }}
                      >
                        Round Off <code>(I)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200, padding: "0 0 5px" }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="RoundOff"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                    <tr
                      style={{
                        borderTop: "1px solid gainsboro",
                        borderBottom: "1px solid gainsboro",
                      }}
                    >
                      <td
                        style={{
                          width: "calc(100% - 210px)",
                          textAlign: "end",
                          fontWeight: 600,
                          padding: "10px 0",
                        }}
                      >
                        Grand Total <code>(J) = (F + G + H)</code>
                      </td>
                      <td style={{ width: "5%", textAlign: "center" }}>:</td>
                      <td style={{ width: 200 }}>
                        <Field
                          wrapperClassName="calculation-label right-aligned-field"
                          wrapperStyle={{ margin: 0, width: "100%" }}
                          name="FinalAmt"
                          placeholder="0"
                          format="n2"
                          component={FormNumericTextField}
                          disabled="true"
                        />
                      </td>
                    </tr>
                  </table>
                </div>
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 10,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={QuotationID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || loading}
                  loading={loading}
                />
                <RippleButton
                  fillMode="outline"
                  themeColor="primary"
                  style={{ marginLeft: 10 }}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </RippleButton>
              </GridLayoutItem>
            </GridLayout>
          </ShadowCard>
          <TotalChangeWatcher formRenderProps={formRenderProps} />
        </FormElement>
      )}
    />
  );
};

export default QuotationInformation;
